import type { MpGameStateSnapshot, MpHighwayStateWire, MpObstacleState } from "../../../lib/multiplayer/protocol";

/** Match server tick rate (room-manager TICK_MS = 1000/30). */
const SERVER_TICK_MS = 1000 / 30;
/** Render slightly in the past for smooth interpolation between snapshots. */
const INTERP_DELAY_MS = 110;
const MAX_SNAPSHOTS = 20;

export type MpPlayerSnapshot = {
  id: string;
  x: number;
  y: number;
  alive: boolean;
  isSpectator: boolean;
  trailEnabled: boolean;
  trailColor: string;
  speedrunPoints?: number[];
};

export type MpBufferedSnapshot = {
  tick: number;
  receivedAt: number;
  score: number;
  players: MpPlayerSnapshot[];
  obstacles: MpObstacleState[];
  highway?: MpHighwayStateWire;
  teamSpeedrunPoints?: number[];
};

export type MpInterpolatedFrame = {
  tick: number;
  score: number;
  players: MpPlayerSnapshot[];
  obstacles: MpObstacleState[];
  highway?: MpHighwayStateWire;
  teamSpeedrunPoints?: number[];
};

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function lerpObstacle(a: MpObstacleState, b: MpObstacleState, t: number): MpObstacleState {
  return {
    x: lerp(a.x, b.x, t),
    y: lerp(a.y, b.y, t),
    vx: lerp(a.vx, b.vx, t),
    vy: lerp(a.vy, b.vy, t),
    r: lerp(a.r, b.r, t),
  };
}

function lerpPlayer(a: MpPlayerSnapshot, b: MpPlayerSnapshot, t: number): MpPlayerSnapshot {
  return {
    id: a.id,
    x: lerp(a.x, b.x, t),
    y: lerp(a.y, b.y, t),
    alive: t < 0.5 ? a.alive : b.alive,
    isSpectator: t < 0.5 ? a.isSpectator : b.isSpectator,
    trailEnabled: t < 0.5 ? a.trailEnabled : b.trailEnabled,
    trailColor: t < 0.5 ? a.trailColor : b.trailColor,
    speedrunPoints: t < 0.5 ? a.speedrunPoints : b.speedrunPoints,
  };
}

function cloneSnapshot(state: MpGameStateSnapshot, receivedAt: number): MpBufferedSnapshot {
  return {
    tick: state.tick,
    receivedAt,
    score: state.score,
    players: state.players.map((p) => ({
      id: p.id,
      x: p.x,
      y: p.y,
      alive: p.alive,
      isSpectator: p.isSpectator,
      trailEnabled: p.trailEnabled !== false,
      trailColor: p.trailColor ?? "#66ffff",
      speedrunPoints: p.speedrunPoints,
    })),
    obstacles: state.obstacles.map((o) => ({ ...o })),
    highway: state.highway
      ? {
          cameraY: state.highway.cameraY,
          nextSpawnWorldY: state.highway.nextSpawnWorldY,
          obstacles: Array.isArray(state.highway.obstacles) ? state.highway.obstacles : [],
        }
      : undefined,
    teamSpeedrunPoints: state.teamSpeedrunPoints
      ? [...state.teamSpeedrunPoints]
      : undefined,
  };
}

function interpolateObstacles(
  a: MpObstacleState[],
  b: MpObstacleState[],
  t: number,
): MpObstacleState[] {
  const n = Math.max(a.length, b.length);
  const out: MpObstacleState[] = [];
  for (let i = 0; i < n; i++) {
    const oa = a[i];
    const ob = b[i];
    if (oa && ob) out.push(lerpObstacle(oa, ob, t));
    else if (ob) out.push({ ...ob });
    else if (oa) out.push({ ...oa });
  }
  return out;
}

function interpolatePlayers(
  a: MpPlayerSnapshot[],
  b: MpPlayerSnapshot[],
  t: number,
): MpPlayerSnapshot[] {
  const byId = new Map<string, MpPlayerSnapshot>();
  for (const p of a) byId.set(p.id, p);
  for (const p of b) {
    const prev = byId.get(p.id);
    if (prev) byId.set(p.id, lerpPlayer(prev, p, t));
    else byId.set(p.id, { ...p });
  }
  return [...byId.values()];
}

export class MpSnapshotBuffer {
  private snapshots: MpBufferedSnapshot[] = [];

  clear(): void {
    this.snapshots = [];
  }

  /** Called when a game_state WebSocket message arrives — does not touch render state. */
  push(state: MpGameStateSnapshot, receivedAt = performance.now()): void {
    const snap = cloneSnapshot(state, receivedAt);
    const last = this.snapshots[this.snapshots.length - 1];
    if (last && last.tick === snap.tick) {
      this.snapshots[this.snapshots.length - 1] = snap;
      return;
    }
    if (last && snap.tick < last.tick) return;
    this.snapshots.push(snap);
    while (this.snapshots.length > MAX_SNAPSHOTS) {
      this.snapshots.shift();
    }
  }

  /** Sample interpolated world state for the current animation frame. */
  sample(now = performance.now()): MpInterpolatedFrame | null {
    if (this.snapshots.length === 0) return null;

    const renderTime = now - INTERP_DELAY_MS;

    if (this.snapshots.length === 1) {
      const s = this.snapshots[0]!;
      return {
        tick: s.tick,
        score: s.score,
        players: s.players,
        obstacles: s.obstacles,
        highway: s.highway,
        teamSpeedrunPoints: s.teamSpeedrunPoints,
      };
    }

    let older: MpBufferedSnapshot | null = null;
    let newer: MpBufferedSnapshot | null = null;

    for (let i = 0; i < this.snapshots.length; i++) {
      const s = this.snapshots[i]!;
      if (s.receivedAt <= renderTime) older = s;
      if (s.receivedAt >= renderTime) {
        newer = s;
        break;
      }
    }

    if (!older) older = this.snapshots[0]!;
    if (!newer) newer = this.snapshots[this.snapshots.length - 1]!;

    if (older === newer || newer.receivedAt <= older.receivedAt) {
      return {
        tick: newer.tick,
        score: newer.score,
        players: newer.players,
        obstacles: newer.obstacles,
        highway: newer.highway ?? older.highway,
        teamSpeedrunPoints: newer.teamSpeedrunPoints ?? older.teamSpeedrunPoints,
      };
    }

    const span = newer.receivedAt - older.receivedAt;
    const t = span > 0 ? Math.max(0, Math.min(1, (renderTime - older.receivedAt) / span)) : 0;

    return {
      tick: newer.tick,
      score: lerp(older.score, newer.score, t),
      players: interpolatePlayers(older.players, newer.players, t),
      obstacles: interpolateObstacles(older.obstacles, newer.obstacles, t),
      highway: newer.highway ?? older.highway,
      teamSpeedrunPoints: newer.teamSpeedrunPoints ?? older.teamSpeedrunPoints,
    };
  }

  latestHighway(): MpHighwayStateWire | undefined {
    const last = this.snapshots[this.snapshots.length - 1];
    return last?.highway;
  }

  latestFrame(): MpInterpolatedFrame | null {
    const s = this.snapshots[this.snapshots.length - 1];
    if (!s) return null;
    return {
      tick: s.tick,
      score: s.score,
      players: s.players,
      obstacles: s.obstacles,
      highway: s.highway,
      teamSpeedrunPoints: s.teamSpeedrunPoints,
    };
  }

  lastReceivedAt(): number {
    const s = this.snapshots[this.snapshots.length - 1];
    return s?.receivedAt ?? 0;
  }

  estimatedServerTickMs(): number {
    return SERVER_TICK_MS;
  }
}

export const mpSnapshotBuffer = new MpSnapshotBuffer();
